import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { timeOffApi } from '../services/api';
import type { TimeOff, TimeOffCreateRequest, TimeOffUpdateRequest } from 'shared';

interface Props {
  block?: TimeOff | null;
  defaultDate?: string;
  onClose: () => void;
}

const OFF_TYPES = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'travel', label: 'Travel' },
  { value: 'personal', label: 'Personal' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'other', label: 'Other' },
] as const;

export function TimeOffModal({ block, defaultDate, onClose }: Props) {
  const queryClient = useQueryClient();
  const isEditing = !!block;

  const today = defaultDate ?? new Date().toISOString().split('T')[0];

  const [title, setTitle] = useState(block?.title ?? 'Day Off');
  const [offType, setOffType] = useState<TimeOff['off_type']>(block?.off_type ?? 'personal');
  const [isFullDay, setIsFullDay] = useState(block ? block.is_full_day : true);
  const [startDate, setStartDate] = useState(block ? block.start_datetime.split('T')[0] : today);
  const [endDate, setEndDate] = useState(block ? block.end_datetime.split('T')[0] : today);
  const [startTime, setStartTime] = useState(() => {
    if (block && !block.is_full_day) return block.start_datetime.split('T')[1]?.slice(0, 5) ?? '08:00';
    return '08:00';
  });
  const [endTime, setEndTime] = useState(() => {
    if (block && !block.is_full_day) return block.end_datetime.split('T')[1]?.slice(0, 5) ?? '12:00';
    return '12:00';
  });
  const [notes, setNotes] = useState(block?.notes ?? '');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['time-off'] });
    onClose();
  };

  const createMutation = useMutation({
    mutationFn: (data: TimeOffCreateRequest) => timeOffApi.create(data),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: (data: TimeOffUpdateRequest) => timeOffApi.update(block!.id, data),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: () => timeOffApi.delete(block!.id),
    onSuccess: invalidate,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const start = isFullDay ? `${startDate}T00:00:00` : `${startDate}T${startTime}:00`;
    const end   = isFullDay ? `${endDate}T23:59:59`   : `${startDate}T${endTime}:00`;

    const payload = {
      title, off_type: offType, is_full_day: isFullDay,
      start_datetime: start, end_datetime: end,
      notes: notes || undefined,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Time Off' : 'Add Time Off'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Vacation, Travel Day"
              required
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div className="flex flex-wrap gap-2">
              {OFF_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setOffType(t.value)}
                  className={`px-3 py-1 text-sm rounded-full border ${offType === t.value ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Full day toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsFullDay(!isFullDay)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isFullDay ? 'bg-blue-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isFullDay ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-gray-700">Full day</span>
          </div>

          {/* Date range */}
          {isFullDay ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (e.target.value > endDate) setEndDate(e.target.value);
                  }}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error instanceof Error ? error.message : 'Something went wrong'}</p>
          )}

          <div className="flex gap-2 pt-2">
            {isEditing && (
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
              >
                Delete
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
